import { useEffect, useLayoutEffect, useState } from "react";
import type { RefObject } from "react";

import { useVirtualizer, useWindowVirtualizer } from "@tanstack/react-virtual";

const ROW_OVERSCAN = 8;
const COLUMN_OVERSCAN = 3;

export const AUTO_ROW_LIMIT = 100;
export const AUTO_COLUMN_LIMIT = 30;

export interface VirtualWindow {
  indexes: number[];
  before: number;
  after: number;
}

export interface RowVirtualizer extends VirtualWindow {
  measureElement: (element: Element | null) => void;
}

const SCROLLS = /(auto|scroll|overlay)/;

// The app scrolls inside a layout container, not the window, so find whatever actually scrolls
const findScrollParent = (
  element: HTMLElement | null,
): HTMLElement | Window => {
  for (let node = element; node; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);
    if (SCROLLS.test(overflowY) && node.scrollHeight > node.clientHeight + 1) {
      return node;
    }
  }
  return window;
};

const offsetWithin = (element: HTMLElement, parent: HTMLElement | Window) =>
  parent === window
    ? element.getBoundingClientRect().top + window.scrollY
    : element.getBoundingClientRect().top -
      (parent as HTMLElement).getBoundingClientRect().top +
      (parent as HTMLElement).scrollTop;

const toWindow = (
  items: { index: number; start: number; end: number }[],
  total: number,
  margin: number,
): VirtualWindow => {
  return {
    indexes: items.map((item) => item.index),
    before: items.length ? items[0].start - margin : 0,
    after: items.length ? total - (items[items.length - 1].end - margin) : 0,
  };
};

/** Rows in view, measured as they render so expanded and wrapped rows keep their real height */
export const useRowVirtualizer = ({
  enabled,
  count,
  scrollRef,
  bodyRef,
  ownScroll,
  rowHeight,
}: {
  enabled: boolean;
  count: number;
  scrollRef: RefObject<HTMLDivElement | null>;
  bodyRef: RefObject<HTMLDivElement | null>;
  ownScroll: boolean;
  /** Starting estimate; rows are measured as they render */
  rowHeight: number;
}): RowVirtualizer | null => {
  const [parent, setParent] = useState<HTMLElement | Window | null>(null);
  const [margin, setMargin] = useState(0);

  useLayoutEffect(() => {
    if (!enabled) {
      setParent(null);
      return;
    }
    setParent(
      ownScroll
        ? scrollRef.current
        : findScrollParent(scrollRef.current?.parentElement ?? null),
    );
  }, [enabled, ownScroll]);

  // Content above the body (header, toolbar, pivot setup) moves where the rows start
  useLayoutEffect(() => {
    if (!parent || !bodyRef.current) return;
    const next = offsetWithin(bodyRef.current, parent);
    if (Math.abs(next - margin) > 1) setMargin(next);
  });

  const isWindow = parent === window;
  const options = {
    count,
    estimateSize: () => rowHeight,
    overscan: ROW_OVERSCAN,
    scrollMargin: margin,
  };
  const inElement = useVirtualizer({
    ...options,
    enabled: enabled && !!parent && !isWindow,
    getScrollElement: () => (isWindow ? null : (parent as HTMLElement | null)),
  });
  const inWindow = useWindowVirtualizer({
    ...options,
    enabled: enabled && isWindow,
  });

  if (!enabled || !parent) return null;
  const virtualizer = isWindow ? inWindow : inElement;
  return {
    ...toWindow(
      virtualizer.getVirtualItems(),
      virtualizer.getTotalSize(),
      margin,
    ),
    measureElement: virtualizer.measureElement,
  };
};

// Unpinned columns in view. Pinned columns always render
export const useColumnVirtualizer = ({
  enabled,
  sizes,
  startWidth,
  scrollRef,
}: {
  enabled: boolean;
  sizes: number[];
  startWidth: number;
  scrollRef: RefObject<HTMLDivElement | null>;
}): VirtualWindow | null => {
  const virtualizer = useVirtualizer({
    enabled,
    horizontal: true,
    count: sizes.length,
    estimateSize: (index) => sizes[index],
    overscan: COLUMN_OVERSCAN,
    scrollMargin: startWidth,
    getScrollElement: () => scrollRef.current,
  });

  const key = sizes.join();
  useEffect(() => {
    if (enabled) virtualizer.measure();
  }, [key, startWidth, enabled]);

  if (!enabled) return null;
  return toWindow(
    virtualizer.getVirtualItems(),
    virtualizer.getTotalSize(),
    startWidth,
  );
};
